import { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { fetchParts, fetchPartsCount, fetchPartsFacets } from "../lib/parts";
import PartCard from "../components/PartCard";
import Seo from "../components/Seo";

const SORT_OPTIONS = [
  { value: "newest", label: "Newest" },
  { value: "price_asc", label: "Price (Low to High)" },
  { value: "price_desc", label: "Price (High to Low)" },
  { value: "name_asc", label: "Name (A–Z)" },
];

/**
 * Public parts catalogue. Filters by category (deep-linked from homepage tiles),
 * make/model, condition, brand. Uses server-side pagination and count.
 */
const PartsList = () => {
  const [params, setParams] = useSearchParams();
  const [parts, setParts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [facets, setFacets] = useState({ categories: [], brands: [], makes: [], conditions: [] });

  const [conditions, setConditions] = useState([]);
  const [brands, setBrands] = useState([]);
  const [priceMax, setPriceMax] = useState(2000);
  const [sort, setSort] = useState("newest");
  const [page, setPage] = useState(1);
  const pageSize = 12;

  const category = params.get("category") || "";
  const make = params.get("make") || "";
  const model = params.get("model") || "";
  const yearParam = params.get("year") || "";
  const year = yearParam ? Number(yearParam) : null;

  const activeCategoryLabel = useMemo(
    () => facets.categories.find((c) => c.slug === category)?.label,
    [facets, category]
  );

  useEffect(() => {
    fetchPartsFacets().then(setFacets).catch(() => {});
  }, []);

  const query = useMemo(() => {
    const q = { sort, page, page_size: pageSize, price_max: priceMax };
    if (category) q.category = category;
    if (make) q.make = make;
    if (model) q.model = model;
    if (year) q.year = year;
    if (conditions.length) q.condition = conditions.join(",");
    if (brands.length) q.brand = brands.join(",");
    return q;
  }, [sort, page, priceMax, category, make, model, year, conditions, brands]);

  useEffect(() => {
    setLoading(true);
    Promise.all([fetchParts(query), fetchPartsCount(query)])
      .then(([list, c]) => { setParts(list); setTotal(c.total || 0); })
      .finally(() => setLoading(false));
  }, [query]);

  useEffect(() => { setPage(1); }, [category, make, model, year, conditions, brands, priceMax, sort]);

  const toggle = (arr, v, setter) => setter(arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const setCategory = (slug) => {
    const next = new URLSearchParams(params);
    if (slug) next.set("category", slug); else next.delete("category");
    setParams(next);
  };

  return (
    <div className="vd-page" data-testid="page-parts">
      <Seo
        title={activeCategoryLabel ? `${activeCategoryLabel} — ValueDrive Auto Solutions` : "Auto Parts"}
        description="Quality used & new auto parts. Engine, suspension, brakes, body panels, electrical, lighting and wheels & tyres — nationwide UK delivery."
        path="/parts"
      />

      <section className="vd-section-secondary py-12 relative overflow-hidden">
        <div className="vd-red-accent-line" style={{ top: "20%" }} aria-hidden="true" />
        <div className="vd-red-accent-line" style={{ bottom: "20%" }} aria-hidden="true" />
        <div className="vd-container relative">
          <h1 className="mb-3 text-center lg:text-left">{activeCategoryLabel ? activeCategoryLabel : "Shop Auto Parts"}</h1>
          <p className="vd-muted max-w-2xl mx-auto lg:mx-0 text-center lg:text-left">
            Tested used and genuine new parts across all major UK manufacturers — fast dispatch, warranty included.
          </p>

          {/* Category chips */}
          <div className="mt-6 flex flex-wrap gap-2 justify-center lg:justify-start" data-testid="parts-category-chips">
            <button
              onClick={() => setCategory("")}
              className={`vd-chip ${!category ? "active" : ""}`}
              data-testid="parts-chip-all"
            >All</button>
            {facets.categories.map((c) => (
              <button
                key={c.slug}
                onClick={() => setCategory(c.slug)}
                className={`vd-chip ${category === c.slug ? "active" : ""}`}
                data-testid={`parts-chip-${c.slug}`}
              >{c.label}</button>
            ))}
          </div>
        </div>
      </section>

      <section className="vd-container py-10 grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-8">
        <aside className="vd-card p-5 h-fit sticky top-[128px]" data-testid="parts-filters">
          <FilterGroup title="Fitment">
            <input className="vd-input mb-2" placeholder="Make (e.g. Ford)" defaultValue={make} onBlur={(e) => {
              const n = new URLSearchParams(params); e.target.value ? n.set("make", e.target.value) : n.delete("make"); setParams(n);
            }} data-testid="filter-make" />
            <input className="vd-input mb-2" placeholder="Model (e.g. Focus)" defaultValue={model} onBlur={(e) => {
              const n = new URLSearchParams(params); e.target.value ? n.set("model", e.target.value) : n.delete("model"); setParams(n);
            }} data-testid="filter-model" />
            <input className="vd-input" type="number" placeholder="Year (e.g. 2018)" defaultValue={yearParam} onBlur={(e) => {
              const n = new URLSearchParams(params); e.target.value ? n.set("year", e.target.value) : n.delete("year"); setParams(n);
            }} data-testid="filter-year" />
          </FilterGroup>

          {facets.conditions.length > 0 && (
            <FilterGroup title="Condition">
              {facets.conditions.map((c) => (
                <label key={c} className="flex items-center gap-3 py-1.5 cursor-pointer">
                  <input type="checkbox" className="vd-checkbox" checked={conditions.includes(c)} onChange={() => toggle(conditions, c, setConditions)} data-testid={`filter-condition-${c.toLowerCase()}`} />
                  <span className="vd-body text-[15px]">{c}</span>
                </label>
              ))}
            </FilterGroup>
          )}

          {facets.brands.length > 0 && (
            <FilterGroup title="Brand / Manufacturer">
              <div className="max-h-52 overflow-y-auto pr-1">
                {facets.brands.map((b) => (
                  <label key={b} className="flex items-center gap-3 py-1 cursor-pointer">
                    <input type="checkbox" className="vd-checkbox" checked={brands.includes(b)} onChange={() => toggle(brands, b, setBrands)} data-testid={`filter-brand-${b.toLowerCase()}`} />
                    <span className="vd-body text-[14px]">{b}</span>
                  </label>
                ))}
              </div>
            </FilterGroup>
          )}

          <FilterGroup title="Max Price">
            <div className="px-1">
              <input type="range" min="20" max="2000" step="10" value={priceMax} onChange={(e) => setPriceMax(Number(e.target.value))} className="vd-range" data-testid="filter-price-range" />
              <div className="flex justify-between vd-muted text-sm mt-1">
                <span>£20</span>
                <span>£{priceMax}</span>
              </div>
            </div>
          </FilterGroup>
        </aside>

        <div>
          <div className="flex items-center justify-between mb-6">
            <p className="vd-muted text-sm" data-testid="parts-results-count">{loading ? "Loading…" : `${total} part${total === 1 ? "" : "s"} found`}</p>
            <div className="flex items-center gap-2">
              <span className="vd-muted text-sm">Sort by:</span>
              <select className="vd-select" style={{ width: "auto" }} value={sort} onChange={(e) => setSort(e.target.value)} data-testid="parts-sort-select">
                {SORT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
          </div>

          {loading && parts.length === 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="parts-loading">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="vd-card aspect-[4/5] animate-pulse" style={{ background: "var(--bg-secondary)" }} />
              ))}
            </div>
          ) : parts.length === 0 ? (
            <div className="vd-card p-10 text-center" data-testid="parts-empty">
              <p className="vd-muted">No parts match your filters. Try widening your search or <Link to="/contact" className="underline">contact us</Link> for stock enquiries.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="parts-grid">
              {parts.map((p) => <PartCard key={p.id} part={p} />)}
            </div>
          )}

          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-10" data-testid="parts-pagination">
              {Array.from({ length: totalPages }).map((_, i) => (
                <button
                  key={i}
                  onClick={() => setPage(i + 1)}
                  className="w-10 h-10 rounded-md border font-semibold transition-colors"
                  style={{
                    borderColor: page === i + 1 ? "var(--primary-red)" : "var(--border-color)",
                    background: page === i + 1 ? "var(--primary-red)" : "var(--card-bg)",
                    color: page === i + 1 ? "#fff" : "var(--text-heading)",
                  }}
                  data-testid={`parts-page-${i + 1}`}
                >{i + 1}</button>
              ))}
            </div>
          )}
        </div>
      </section>

      <style>{`
        .vd-chip { padding: 6px 14px; border-radius: 999px; border: 1px solid var(--border-color); background: var(--card-bg); font-size: 13px; font-weight: 600; color: var(--text-heading); transition: all 150ms ease; }
        .vd-chip:hover { border-color: var(--primary-red); }
        .vd-chip.active { background: var(--primary-red); border-color: var(--primary-red); color: #fff; }
      `}</style>
    </div>
  );
};

const FilterGroup = ({ title, children }) => (
  <div className="mb-6">
    <h4 className="vd-heading text-sm uppercase tracking-wider mb-3" style={{ letterSpacing: "0.08em" }}>{title}</h4>
    {children}
  </div>
);

export default PartsList;
